// Yoga.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// === IMAGES ===
// Replace with your actual yoga-themed hero image
import heroBg from '../assets/Resort/131.png';  // ← change path/filename as needed
import yogaImpImg from '../assets/Resort/128.jpg'; // ← change path/filename as needed
import yogaPraImg from '../assets/Resort/130.jpeg'; // ← change path/filename as needed
import meditationImage from '../assets/River/52.jpeg'; // ← change path/filename as needed

// === MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const scaleUp = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};

const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.2 },
    },
};

const accordionItem = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const Yoga = () => {
    const { t } = useTranslation('yoga');

    // === DATA ===
    const yogaImpDetails = [
        t('importanceText1'),
        t('importanceText2'),
        t('importanceText3')
    ];

    const yogaPraDetails = [
        t('practiceText1'),
        t('practiceText2'),
        t('practiceText3'),
        t('practiceText4')
    ];
    const dataArray = [
        {
            title: t('meditationTitle'),
            desc: t('meditationDesc')
        }
    ];

    // === STATE FOR ACCORDION ===
    const [activeIndex, setActiveIndex] = React.useState(null);

    const toggleService = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <div className="bg-[#1F1F1F] min-h-screen">
            {/* ===== HERO SECTION ===== */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative flex h-[60vh] w-full items-center justify-center font-lato md:h-[70vh] lg:h-[85vh]"
            >
                <img
                    src={heroBg}
                    alt={t('heroAlt')}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#020202c7] via-[#00000080] to-[#42424259]" />

                <div className="container relative z-10 flex items-center justify-center pt-16 md:pt-20">
                    <div className="w-full max-w-4xl text-center">
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold leading-tight text-white font-copperplate">
                            {t('heroTitle')}
                        </h1>
                        <p className="mt-6 text-lg md:text-2xl lg:text-3xl font-semibold tracking-wide text-[#96BA33] font-lato">
                            {t('heroSubtitle')}
                        </p>

                        {/* Optional decorative SVG lines – remove if you don't like them */}
                        <div className="absolute left-8 top-10 hidden lg:block">
                            <svg width="289" height="245" viewBox="0 0 289 245" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M289 1H1V244.5" stroke="white" strokeWidth="2" />
                            </svg>
                        </div>
                        <div className="absolute -bottom-12 right-8 hidden lg:block">
                            <svg width="289" height="244" viewBox="0 0 289 244" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 243.5H288V0" stroke="white" strokeWidth="2" />
                            </svg>
                        </div>
                    </div>
                </div>
            </motion.div>

            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="relative bg-gradient-to-b from-[#1F1F1F] via-[#2a2a2a] to-[#1F1F1F] py-20 md:py-32 overflow-hidden"
            >
                {/* Decorative elements */}
                <div className="absolute top-0 left-0 w-64 h-64 bg-[#96BA33] rounded-full mix-blend-multiply filter blur-3xl opacity-10 -translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 right-0 w-72 h-72 bg-[#96BA33] rounded-full mix-blend-multiply filter blur-3xl opacity-10 translate-x-1/4 translate-y-1/4"></div>

                <div className="container mx-auto px-6 lg:px-8 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-16 md:mb-20"
                    >
                        <span className="inline-block text-[#96BA33] font-semibold text-sm md:text-base uppercase tracking-widest mb-4 font-lato">{t('importanceEyebrow')}</span>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 ">
                            {t('importanceTitle')}
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-[#96BA33] to-transparent mx-auto"></div>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
                        {/* Image on LEFT */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="group"
                        >
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl transform transition-transform duration-500 group-hover:scale-105">
                                <img
                                    src={yogaImpImg}
                                    alt={t('importanceImageAlt')}
                                    className="w-full h-auto object-cover"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            </div>
                        </motion.div>

                        {/* Details on RIGHT */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="space-y-8"
                        >
                            {yogaImpDetails.map((point, i) => (
                                <motion.p
                                    key={i}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
                                    className="text-white text-lg md:text-xl leading-relaxed font-inknut backdrop-blur-sm bg-white/5 p-6 rounded-2xl border border-white/10 hover:border-[#96BA33]/30 transition-all duration-300"
                                >
                                    {point}
                                </motion.p>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="relative bg-gradient-to-b from-[#1F1F1F] via-[#252525] to-[#1F1F1F] py-20 md:py-32 overflow-hidden"
            >
                {/* Decorative elements */}
                <div className="absolute top-20 right-0 w-80 h-80 bg-[#96BA33] rounded-full mix-blend-multiply filter blur-3xl opacity-10 translate-x-1/3"></div>
                <div className="absolute bottom-10 left-0 w-72 h-72 bg-[#96BA33] rounded-full mix-blend-multiply filter blur-3xl opacity-10 -translate-x-1/2"></div>

                <div className="container mx-auto px-6 lg:px-8 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-16 md:mb-20"
                    >
                        <span className="inline-block text-[#96BA33] font-semibold text-sm md:text-base uppercase tracking-widest mb-4 font-lato">{t('practiceEyebrow')}</span>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 ">
                            {t('practiceTitle')}
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#96BA33] to-transparent mx-auto"></div>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="space-y-8"
                        >
                            {yogaPraDetails.map((point, i) => (
                                <motion.p
                                    key={i}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
                                    className="text-white text-lg md:text-xl leading-relaxed font-inknut backdrop-blur-sm bg-white/5 p-6 rounded-2xl border border-white/10 hover:border-[#96BA33]/30 transition-all duration-300"
                                >
                                    {point}
                                </motion.p>
                            ))}
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="group"
                        >
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl transform transition-transform duration-500 group-hover:scale-105">
                                <img
                                    src={yogaPraImg}
                                    alt={t('practiceImageAlt')}
                                    className="w-full h-auto object-cover"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            <section className="relative bg-gradient-to-b from-[#1F1F1F] via-[#2a2a2a] to-[#1a1a1a] py-20 md:py-32 overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#96BA33] rounded-full mix-blend-multiply filter blur-3xl opacity-10 -translate-y-1/2"></div>
                <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#96BA33] rounded-full mix-blend-multiply filter blur-3xl opacity-10 translate-y-1/2"></div>

                <div className="container mx-auto px-6 lg:px-8 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="mb-16 md:mb-20"
                    >
                        <div className="flex items-center gap-4 mb-6">
                            <div className="h-1 w-12 bg-gradient-to-r from-[#96BA33] to-transparent rounded-full"></div>
                            <span className="text-[#96BA33] font-semibold text-sm md:text-base uppercase tracking-widest font-lato">{t('spiritualEyebrow')}</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white  mb-2">
                            {t('spiritualTitle')}
                        </h1>
                        <p className="text-gray-400 text-lg md:text-xl font-lato mt-4 max-w-2xl">{t('spiritualSubtitle')}</p>
                        <div className="mt-8 h-px w-full bg-gradient-to-r from-[#96BA33]/20 via-[#96BA33]/40 to-[#96BA33]/20"></div>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-stretch">
                        {/* Single static image – left side */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            viewport={{ once: true, amount: 0.3 }}
                            className="group"
                        >
                            <div className="relative h-[500px] md:h-[600px] lg:h-full rounded-3xl overflow-hidden shadow-2xl transform transition-transform duration-500 group-hover:scale-105">
                                <img
                                    src={meditationImage}
                                    alt={t('meditationImageAlt')}
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                    onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/600x750?text=Meditation+Image';
                                    }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500"></div>
                                <div className="absolute inset-0 border border-white/10 rounded-3xl pointer-events-none"></div>
                            </div>
                        </motion.div>

                        {/* Services Accordion – right side */}
                        <motion.div
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.2 }}
                            className="flex flex-col space-y-5"
                        >
                            {dataArray.map((item, index) => (
                                <motion.div
                                    key={index}
                                    variants={accordionItem}
                                    className="group cursor-pointer rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-6 md:p-7 transition-all duration-300 hover:border-[#96BA33]/50 hover:shadow-lg hover:shadow-[#96BA33]/20 hover:bg-gradient-to-br hover:from-white/10 hover:to-white/0 backdrop-blur-sm"
                                    onClick={() => toggleService(index)}
                                    role="button"
                                    aria-expanded={activeIndex === index}
                                    aria-controls={`service-${index}`}
                                >
                                    <h2 className="flex items-center justify-between text-base md:text-lg lg:text-xl font-semibold text-white">
                                        <span className="flex items-center gap-3">
                                            <span className="h-2 w-2 rounded-full bg-[#96BA33] group-hover:scale-150 transition-transform"></span>
                                            {item.title}
                                        </span>
                                        <span className="text-[22px] md:text-[28px] text-[#96BA33] transition-transform duration-300 group-hover:scale-125">
                                            {activeIndex === index ? '−' : '+'}
                                        </span>
                                    </h2>
                                    {activeIndex === index && (
                                        <motion.p
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                            id={`service-${index}`}
                                            className="mt-4 text-[15px] md:text-[16px] text-gray-300 leading-relaxed font-inknut"
                                        >
                                            {item.desc}
                                        </motion.p>
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Yoga;